import { useAtomValue, useSetAtom } from 'jotai';
import { comparisonSelectedPositionsFilterParamsAtom } from '@/atoms/filters/highLevel/comparisonPositions';
import { unwrappedPlayerComparisonSelectedGamesForQueryAtom } from '@/atoms/filters/player/playerComparisonGames';
import { playerComparisonPositionsFilterDebounceObject } from '@/atoms/queries/player/playerComparisonPositions';
import { playerComparisonRadarFilterDebounceObject } from '@/atoms/queries/player/playerComparisonRadar';
import { playerComparisonEventFilterDebounceObject } from '@/atoms/queries/player/playerEventDataComparison';
import { DEFAULT_DEBOUNCE_MS } from '@/consts/api';
import { toObject } from '@/utils/object';
import { useDeepEffect } from '../useDeepEffect';

export const useSetPlayerComparisonDebouncedFilterParams = (debounceTimeout: number = DEFAULT_DEBOUNCE_MS) => {
  const gameIds = useAtomValue(unwrappedPlayerComparisonSelectedGamesForQueryAtom);
  const positions = useAtomValue(comparisonSelectedPositionsFilterParamsAtom);

  const { debouncedValueAtom: comparisonPositionsFilterAtom, timeoutDelayAtom: comparisonPositionsTimeoutDelayAtom } =
    playerComparisonPositionsFilterDebounceObject;
  const setComparisonPositionsFilterTimeoutDelay = useSetAtom(comparisonPositionsTimeoutDelayAtom);
  const setComparisonPositionsFilter = useSetAtom(comparisonPositionsFilterAtom);

  const { debouncedValueAtom: comparisonRadarFilterAtom, timeoutDelayAtom: comparisonRadarTimeoutDelayAtom } =
    playerComparisonRadarFilterDebounceObject;
  const setComparisonRadarFilterTimeoutDelay = useSetAtom(comparisonRadarTimeoutDelayAtom);
  const setComparisonRadarFilter = useSetAtom(comparisonRadarFilterAtom);

  const { debouncedValueAtom: comparisonEventFilterAtom, timeoutDelayAtom: comparisonEventTimeoutDelayAtom } =
    playerComparisonEventFilterDebounceObject;
  const setComparisonEventFilterTimeoutDelay = useSetAtom(comparisonEventTimeoutDelayAtom);
  const setComparisonEventFilter = useSetAtom(comparisonEventFilterAtom);

  const updateTimeouts = (timeout: number) => {
    setComparisonPositionsFilterTimeoutDelay(timeout);
    setComparisonRadarFilterTimeoutDelay(timeout);
    setComparisonEventFilterTimeoutDelay(timeout);
  };

  useDeepEffect(() => {
    // If there's no games, set the timeout to 0 as this won't trigger an API call.
    if (!gameIds.length) updateTimeouts(0);
    setComparisonPositionsFilter({
      in: { game_id: gameIds },
    });
    setComparisonRadarFilter({
      in: {
        ...toObject('position', positions),
        game_id: gameIds,
      },
    });
    setComparisonEventFilter(gameIds.length ? { in: { 'event.game_id': gameIds } } : null);
    // If there were games this time, set the timeout so that we debounce subsequent calls
    if (gameIds.length) updateTimeouts(debounceTimeout);
  }, [gameIds, positions]);
};
